/**
 * Account progression: one XP pool fed by every game, one level shown in the header, and the
 * dungeon classes it unlocks. All amounts live here so tuning is a one-file change.
 */
import type { ClassId } from '../dungeon/types'

export const XP_REWARDS = {
  dungeonDaily: { perFloor: 10, perKill: 2, win: 150, min: 5, streakPerDay: 5, streakCap: 10 },
  dungeonPractice: { ratio: 0.25, dailyCap: 100 },
  duelPvp: { win: 20, loss: 5 },
  duelCpu: { win: 30, draw: 10, loss: 0 },
  iceRace: { first: 30, finished: 10, raced: 5 },
  cycles: { winner: 30, other: 10 },
} as const

export const MAX_LEVEL = 50

/** Total XP needed to *be* level `n`: 0, 100, 300, 600, 1000… */
export function xpForLevel(level: number): number {
  return 50 * level * (level - 1)
}

export function levelFromXp(xp: number): number {
  let level = 1
  while (level < MAX_LEVEL && xp >= xpForLevel(level + 1)) level++
  return level
}

export const UNLOCKS: readonly { level: number; cls: ClassId }[] = [
  { level: 1, cls: 'knight' },
  { level: 3, cls: 'rogue' },
  { level: 6, cls: 'mage' },
]

export function unlockedClasses(level: number): ClassId[] {
  return UNLOCKS.filter((u) => u.level <= level).map((u) => u.cls)
}

const TITLES: readonly [number, string][] = [
  [20, 'Legend'],
  [10, 'Champion'],
  [6, 'Veteran'],
  [3, 'Adventurer'],
  [1, 'Novice'],
]
export function titleFor(level: number): string {
  return TITLES.find(([min]) => level >= min)?.[1] ?? 'Novice'
}

export interface ProgressView {
  xp: number
  level: number
  /** XP into the current level and the size of the level. */
  levelXp: number
  nextLevelXp: number
  streak: number
  title: string
  unlocked: ClassId[]
}

export function progressView(xp: number, streak: number): ProgressView {
  const level = levelFromXp(xp)
  const floor = xpForLevel(level)
  const ceil = level >= MAX_LEVEL ? floor : xpForLevel(level + 1)
  return {
    xp,
    level,
    levelXp: xp - floor,
    nextLevelXp: Math.max(1, ceil - floor),
    streak,
    title: titleFor(level),
    unlocked: unlockedClasses(level),
  }
}

/** XP a finished dungeon run is worth, before the practice ratio and streak bonus. */
export function dungeonRunXp(run: { floorsCleared: number; kills: number; won: boolean }): number {
  const r = XP_REWARDS.dungeonDaily
  return Math.max(
    r.min,
    r.perFloor * run.floorsCleared + r.perKill * run.kills + (run.won ? r.win : 0),
  )
}
